// useSavedFilters — reactive wrapper over the saved-filters API for a single
// view_key. Owns the loaded rows, the precedence resolution used to seed a page
// on load, and a debounced "last used" auto-save.
//
// Precedence for what a page shows on load (highest first):
//   1. ?view=<id> route param   — an explicit "open this preset" navigation
//   2. URL filter params        — a deep link / bookmark the user is on right now
//   3. the user's default preset — a stored "always start here" choice
//   4. the auto-saved last-used  — implicit restore of what they had (OTC-1718)
//   5. nothing                   — page falls back to its own defaults
//
// resolveInitialFilters is exported as a pure function so the precedence logic
// is unit-testable without any network or Vue lifecycle.

import { ref, computed } from 'vue'
import {
  fetchSavedFilters,
  saveLastUsedFilters,
  createPreset,
  renamePreset,
  deletePreset,
  setPresetDefault,
  type SavedFilterRow,
} from '@/util/savedFiltersApi'
import { resolveInitialFilters, rowMatchesEntity, stableStringify, type ResolveInput, type ResolveResult } from '@/util/savedFiltersPrecedence'

export { resolveInitialFilters, rowMatchesEntity } from '@/util/savedFiltersPrecedence'
export const LAST_USED_SAVE_DEBOUNCE_MS = 1200

/**
 * @param viewKey  stable storage key for the page (see savedViewsRegistry)
 * @param entityId getter for the current entity id on a per-entity page; rows saved
 *                 for a different entity are hidden from presets and last-used.
 */
export function useSavedFilters(viewKey: string, entityId?: () => string | number | null | undefined) {
  const rows = ref<SavedFilterRow[]>([])
  const loaded = ref(false)
  const loadError = ref(false)

  const scopeId = computed<string | null>(() => {
    const v = entityId ? entityId() : null
    return v == null || v === '' ? null : String(v)
  })
  const scopedRows = computed(() => rows.value.filter((r) => rowMatchesEntity(r.filters, scopeId.value)))

  const presets = computed(() => scopedRows.value.filter((r) => r.name !== ''))
  const lastUsed = computed<SavedFilterRow | null>(() => scopedRows.value.find((r) => r.name === '') || null)
  const defaultPreset = computed<SavedFilterRow | null>(() => presets.value.find((r) => r.is_default === 1) || null)

  async function load(): Promise<void> {
    try {
      const all = await fetchSavedFilters()
      rows.value = all.filter((r) => r.view_key === viewKey)
      loadError.value = false
    } catch (e) {
      // Resilient: a failed load must not blank the page. Callers fall back to
      // URL/defaults via resolve() returning source 'none'.
      loadError.value = true
    } finally {
      loaded.value = true
    }
  }

  function resolve(input: Omit<ResolveInput, 'presets' | 'lastUsed'>): ResolveResult {
    return resolveInitialFilters({ ...input, presets: presets.value, lastUsed: lastUsed.value })
  }

  // --- Debounced last-used auto-save ---
  let saveTimer: ReturnType<typeof setTimeout> | null = null
  let lastSavedJson: string | null = null

  function saveLastUsed(filters: Record<string, any>): void {
    const json = stableStringify(filters)
    // Skip when nothing changed since the last persisted state (covers the initial
    // restore emit re-saving what we just loaded).
    if (json === lastSavedJson) return
    if (saveTimer) clearTimeout(saveTimer)
    saveTimer = setTimeout(async () => {
      lastSavedJson = json
      try {
        await saveLastUsedFilters(viewKey, filters)
      } catch (e) {
        // A failed autosave is non-fatal; allow a retry on the next change.
        lastSavedJson = null
      }
    }, LAST_USED_SAVE_DEBOUNCE_MS)
  }

  /** Seed the dedupe baseline so restoring loaded last-used doesn't trigger a redundant save. */
  function primeLastUsedBaseline(filters: Record<string, any> | null): void {
    lastSavedJson = filters ? stableStringify(filters) : null
  }

  async function savePreset(name: string, filters: Record<string, any>): Promise<number | null> {
    const id = await createPreset(viewKey, name, filters)
    await load()
    return id
  }

  async function rename(id: number, name: string): Promise<void> {
    await renamePreset(id, name)
    await load()
  }

  async function remove(id: number): Promise<void> {
    await deletePreset(id)
    await load()
  }

  async function setDefault(id: number, isDefault: boolean): Promise<void> {
    await setPresetDefault(id, isDefault)
    await load()
  }

  return {
    rows,
    loaded,
    loadError,
    presets,
    lastUsed,
    defaultPreset,
    load,
    resolve,
    saveLastUsed,
    primeLastUsedBaseline,
    savePreset,
    rename,
    remove,
    setDefault,
  }
}
